// services/unreadStore — the unread-badge state machine for GROUP rooms
// (LANE1_PLAN.md §1). Flag: FEATURES.liveV2Unread. Database side: migration
// 0076_room_unread_counters.sql.
//
// THE RULE: the database is the truth; a broadcast is only a hint that carries
// the ABSOLUTE new row with a version. Nothing here ever adds or subtracts.
//
// Two layers, so the logic can be tested without a network or a DOM:
//   • UnreadCore        — pure state: rows, versions, pending optimistic reads,
//                         the active conversation. No timers, no I/O, no clock
//                         except the `nowMs` handed to `display` for mute expiry.
//   • createUnreadStore — the controller: truth re-reads (throttled, coalesced),
//                         the debounced mark-read with retry, cross-tab relay,
//                         the safety timer. Every side effect is injected.
//
// ONLY SERVER TIMESTAMPS ARE COMPARED. A read cursor is the `created_at` of a
// message the server handed us; the thing it is compared with is the
// `last_read_at` the server sent back. The local clock is used for delays and
// for "is the mute still running" (as services/roomMuteState already does) —
// never to decide whether something was read.
//
// MILLISECONDS. The server stamps in microseconds, the apps keep milliseconds
// (incident 2026-09-21). Every server instant is floored to its millisecond on
// the way in (`serverMs`), and a cursor is sent as the END of its millisecond
// (`cursorIsoEndOfMs`), so a message stamped …583854 is covered by a read at
// …583 on any database, with or without migration 0075.

import type { RoomUnreadRow } from './roomActionsParity';

// ── wire shapes ─────────────────────────────────────────────────────────────
/** Payload of the `unread_state` broadcast = one whole counter row. */
export interface UnreadHint {
  room_id: string;
  unread: number;
  mentions: number;
  last_read_at: string | null;
  version: number;
}
/** One row of `my_room_unread_counters()`. */
export interface UnreadTruthRow extends UnreadHint {
  muted_until?: string | null;
  hints_enabled?: boolean | null;
}

/** Server instant → epoch ms, floored. Tolerates microseconds and any offset. */
export function serverMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  // ECMAScript only promises three fraction digits; cut the rest ourselves.
  // Postgres text output also writes a bare hour offset ("+00").
  const t = Date.parse(
    String(iso).replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1').replace(/([+-]\d{2})$/, '$1:00'),
  );
  return Number.isFinite(t) ? t : null;
}

/** The END of the millisecond, as the server should store a read cursor. */
export function cursorIsoEndOfMs(ms: number): string {
  return new Date(ms).toISOString().replace(/Z$/, '999Z');
}

// ── pure core ───────────────────────────────────────────────────────────────
interface RowState {
  roomId: string;
  unread: number;
  mentions: number;
  lastReadMs: number | null;
  version: number;
  mutedUntilMs: number | null;
  /** Local sequence number of the last change, to order hints against re-reads. */
  seq: number;
}

export interface PendingRead {
  /** Server timestamp (ms) of the newest message this person has been shown. */
  cursorMs: number;
  /** `local` = this tab owns the write; `remote` = another tab of this browser does. */
  origin: 'local' | 'remote';
  /** The write came back OK; the next truth read is authoritative whatever it says. */
  settled: boolean;
}

export interface ActiveConversation {
  roomId: string;
  /** Tab visible AND window focused. */
  visible: boolean;
  atBottom: boolean;
}

export class UnreadCore {
  private rows = new Map<string, RowState>();
  private pending = new Map<string, PendingRead>();
  private active: ActiveConversation | null = null;
  private seq = 0;

  /** Call BEFORE a truth read starts; hand the token to `applyTruth`. */
  beginTruth(): number {
    return this.seq;
  }

  /**
   * Replace the state with a truth snapshot. Per room, a row we already hold
   * with a HIGHER version wins (a hint overtook the read). A room missing from
   * the snapshot is dropped — unless a hint created/updated it after the read
   * began (joined a room while the request was in flight).
   */
  applyTruth(truth: UnreadTruthRow[], token: number): void {
    const seen = new Set<string>();
    for (const t of truth) {
      if (!t || !t.room_id) continue;
      const roomId = String(t.room_id);
      seen.add(roomId);
      const held = this.rows.get(roomId);
      const version = Number(t.version) || 0;
      const mutedUntilMs = serverMs(t.muted_until ?? null);
      if (held && held.version > version) {
        held.mutedUntilMs = mutedUntilMs;
        continue;
      }
      this.rows.set(roomId, {
        roomId,
        unread: Math.max(0, Number(t.unread) || 0),
        mentions: Math.max(0, Number(t.mentions) || 0),
        lastReadMs: serverMs(t.last_read_at),
        version,
        mutedUntilMs,
        seq: ++this.seq,
      });
    }
    for (const [roomId, row] of [...this.rows]) {
      if (!seen.has(roomId) && row.seq <= token) this.rows.delete(roomId);
    }
    // Pending reads: the truth settles them.
    for (const [roomId, p] of [...this.pending]) {
      const row = this.rows.get(roomId);
      if (!row || p.settled || (row.lastReadMs !== null && row.lastReadMs >= p.cursorMs)) {
        this.pending.delete(roomId);
      }
    }
  }

  /**
   * Apply one hint. Idempotent and order-proof: a version we already hold (or
   * an older one) changes nothing. Returns whether anything changed.
   */
  applyHint(h: UnreadHint | null | undefined): boolean {
    if (!h || !h.room_id) return false;
    const version = Number(h.version);
    if (!Number.isFinite(version)) return false;
    const roomId = String(h.room_id);
    const held = this.rows.get(roomId);
    if (held && version <= held.version) return false;
    const lastReadMs = serverMs(h.last_read_at);
    this.rows.set(roomId, {
      roomId,
      unread: Math.max(0, Number(h.unread) || 0),
      mentions: Math.max(0, Number(h.mentions) || 0),
      lastReadMs,
      version,
      mutedUntilMs: held?.mutedUntilMs ?? null,
      seq: ++this.seq,
    });
    const p = this.pending.get(roomId);
    if (p && lastReadMs !== null && lastReadMs >= p.cursorMs) this.pending.delete(roomId);
    return true;
  }

  /** `room_removed`: the person is no longer in the room. */
  removeRoom(roomId: string): boolean {
    this.pending.delete(roomId);
    if (this.active?.roomId === roomId) this.active = null;
    return this.rows.delete(roomId);
  }

  /**
   * Optimistic read: the badge is 0 from THIS call on. A cursor never moves
   * back; one the server already covers is not recorded at all.
   * Returns true when a (new or later) pending cursor was recorded.
   */
  optimisticRead(roomId: string, cursorMs: number, origin: 'local' | 'remote'): boolean {
    if (!roomId || !(cursorMs > 0)) return false;
    const row = this.rows.get(roomId);
    if (row && row.lastReadMs !== null && row.lastReadMs >= cursorMs) return false;
    const held = this.pending.get(roomId);
    if (held && held.cursorMs >= cursorMs) {
      // Same or older cursor: only an upgrade from "another tab writes it" to
      // "this tab writes it" is worth recording.
      if (origin === 'local' && held.origin === 'remote') { held.origin = 'local'; held.settled = false; return true; }
      return false;
    }
    this.pending.set(roomId, { cursorMs, origin, settled: false });
    return true;
  }

  getPending(roomId: string): PendingRead | undefined {
    return this.pending.get(roomId);
  }

  /** The write for this cursor came back OK. */
  settlePending(roomId: string, cursorMs: number): void {
    const p = this.pending.get(roomId);
    if (p && p.cursorMs <= cursorMs) p.settled = true;
  }

  /** Drop a pending read (write failed for a real reason / expired). */
  dropPending(roomId: string, cursorMs?: number): boolean {
    const p = this.pending.get(roomId);
    if (!p) return false;
    if (cursorMs !== undefined && p.cursorMs > cursorMs) return false;
    return this.pending.delete(roomId);
  }

  serverCursorMs(roomId: string): number | null {
    return this.rows.get(roomId)?.lastReadMs ?? null;
  }

  setActive(a: ActiveConversation | null): void {
    this.active = a;
  }

  getActive(): ActiveConversation | null {
    return this.active;
  }

  /** Is this the conversation the person is looking at, live, at the newest message? */
  isWatching(roomId: string): boolean {
    const a = this.active;
    return !!a && a.roomId === roomId && a.visible && a.atBottom;
  }

  setMuted(roomId: string, untilMs: number | null): boolean {
    const row = this.rows.get(roomId);
    if (!row) {
      if (untilMs === null) return false;
      // A muted room with nothing unread still shows its bell.
      this.rows.set(roomId, {
        roomId, unread: 0, mentions: 0, lastReadMs: null, version: -1, mutedUntilMs: untilMs, seq: ++this.seq,
      });
      return true;
    }
    if (row.mutedUntilMs === untilMs) return false;
    row.mutedUntilMs = untilMs;
    return true;
  }

  /**
   * What the badges show. Same shape and same omission rule as today's summary
   * (a room with nothing unread, no mention and no mute is left out).
   *   watching the room            → 0 (rendered on screen, §1.5)
   *   pending read not yet covered → 0 (a stale hint must not resurrect it, §1.4)
   *   otherwise                    → the server's number
   */
  display(nowMs: number): Record<string, RoomUnreadRow> {
    const out: Record<string, RoomUnreadRow> = {};
    for (const row of this.rows.values()) {
      const p = this.pending.get(row.roomId);
      const covered = row.lastReadMs !== null && p !== undefined && row.lastReadMs >= p.cursorMs;
      const zero = this.isWatching(row.roomId) || (p !== undefined && !covered);
      const unread = zero ? 0 : row.unread;
      const mentionCount = zero ? 0 : row.mentions;
      const muted = row.mutedUntilMs !== null && row.mutedUntilMs > nowMs;
      if (unread > 0 || mentionCount > 0 || muted) {
        out[row.roomId] = { roomId: row.roomId, unread, mentionCount, muted };
      }
    }
    return out;
  }
}

/** Totals are always DERIVED from the rows — never transmitted, never stored. */
export function deriveUnreadTotals(rows: Record<string, RoomUnreadRow>): {
  rooms: number; messages: number; mentions: number;
} {
  let rooms = 0; let messages = 0; let mentions = 0;
  for (const r of Object.values(rows)) {
    if (r.unread > 0) rooms += 1;
    messages += r.unread;
    mentions += r.mentionCount;
  }
  return { rooms, messages, mentions };
}

// ── controller ──────────────────────────────────────────────────────────────
export type UnreadStatus =
  /** Not started (no user). */
  | 'idle'
  /** First truth read in flight. */
  | 'loading'
  /** Server truth held; the rows can be shown. */
  | 'ready'
  /** Migration missing or the read failed before ever succeeding: use today's path. */
  | 'unavailable';

export type CrossTabMessage =
  | { type: 'read'; userId: string; roomId: string; cursorMs: number }
  | { type: 'read_failed'; userId: string; roomId: string; cursorMs: number };

export interface CrossTab {
  post(msg: CrossTabMessage): void;
  /** Returns an unsubscribe. */
  listen(cb: (msg: CrossTabMessage) => void): () => void;
  close?(): void;
}

export interface UnreadStoreDeps {
  /** `my_room_unread_counters()`. Rejects with the PostgREST error object. */
  fetchTruth: () => Promise<UnreadTruthRow[]>;
  /** `mark_room_read(room, cursor)`. Rejects with the error object. */
  markRead: (roomId: string, cursorMs: number) => Promise<void>;
  /** "The table/function is not there" — from services/roomActionsParity. */
  isMissingSchema: (e: any) => boolean;
  /** A failure worth retrying (offline, 5xx, timeout). Default: everything that is not missing-schema and has no 4xx-ish code. */
  isTransient?: (e: any) => boolean;
  crossTab?: CrossTab | null;
  /** Local clock — delays and mute expiry only. */
  now?: () => number;
  /** Tab visible? The safety timer only re-reads while it is. */
  isVisible?: () => boolean;
  /** Are hints reaching this tab (channel joined)? */
  channelLive?: () => boolean;
}

export const UNREAD_MARK_READ_DEBOUNCE_MS = 1000;
export const UNREAD_TRUTH_MIN_GAP_MS = 2000;
export const UNREAD_SAFETY_MS = 2 * 60 * 1000;
/** A settled write whose hint never showed up: re-read after this long. */
export const UNREAD_SETTLE_RECHECK_MS = 3000;
/** Another tab said "read" and then went quiet: stop believing it after this long. */
export const UNREAD_REMOTE_PENDING_MS = 15000;
export const UNREAD_WRITE_RETRIES = 5;
/** While unavailable, probe again on every Nth safety tick (5 × 2 min = 10 min). */
export const UNREAD_UNAVAILABLE_PROBE_TICKS = 5;

export interface ActiveReport extends ActiveConversation {
  /** Server timestamp (ms) of the newest message on screen; 0 = none yet. */
  newestMs: number;
  /** False holds the cursor WRITE back (RoomChat: until the unread divider anchor is read). */
  canWrite?: boolean;
}

export interface UnreadStore {
  start(userId: string): void;
  stop(): void;
  status(): UnreadStatus;
  /** Whether this database publishes hints (false → rely on re-reads). null = not known yet. */
  hintsEnabled(): boolean | null;
  subscribe(fn: () => void): () => void;
  /** Stable reference between changes (safe for useSyncExternalStore). */
  getRows(): Record<string, RoomUnreadRow>;
  /** Re-read the truth (throttled + coalesced). */
  refresh(): void;
  /** The rooms list changed. Only re-reads when hints are NOT live. */
  noteRoomsChanged(): void;
  onHint(payload: unknown): void;
  onRoomRemoved(payload: unknown): void;
  reportActive(report: ActiveReport): void;
  clearActive(roomId: string): void;
  setMuted(roomId: string, untilMs: number | null): void;
  /** The tab became visible / the network came back / the channel (re)joined. */
  wake(): void;
}

const defaultTransient = (e: any): boolean => {
  const code = String(e?.code ?? '');
  // PostgREST / Postgres codes are verdicts; no code at all is a network failure.
  if (!code) return true;
  return /^(5\d\d|08|57|53)/.test(code) || code === 'PGRST301' || code === '40001' || code === '40P01';
};

export function createUnreadStore(deps: UnreadStoreDeps): UnreadStore {
  const now = deps.now ?? (() => Date.now());
  const isVisible = deps.isVisible ?? (() => true);
  const channelLive = deps.channelLive ?? (() => false);
  const isTransient = deps.isTransient ?? defaultTransient;

  let core = new UnreadCore();
  let userId: string | null = null;
  let status: UnreadStatus = 'idle';
  let hints: boolean | null = null;
  /** `unavailable` because the migration is not there (as opposed to one failed read). */
  let schemaMissing = false;
  let rows: Record<string, RoomUnreadRow> = {};
  let run = 0; // bumped by start/stop so late async work of an old user is dropped
  const subs = new Set<() => void>();

  let truthInFlight = false;
  let truthQueued = false;
  let truthLastAt = -Infinity;
  let truthTimer: ReturnType<typeof setTimeout> | null = null;
  let safetyTimer: ReturnType<typeof setInterval> | null = null;
  let safetyTicks = 0;
  let offCrossTab: (() => void) | null = null;

  /** Per room: the debounce / retry timer of the cursor write, and the latest report. */
  const writeTimers = new Map<string, ReturnType<typeof setTimeout>>();
  const remoteTimers = new Map<string, ReturnType<typeof setTimeout>>();
  const canWrite = new Map<string, boolean>();

  const sameRows = (a: Record<string, RoomUnreadRow>, b: Record<string, RoomUnreadRow>): boolean => {
    const ka = Object.keys(a);
    if (ka.length !== Object.keys(b).length) return false;
    for (const k of ka) {
      const x = a[k]; const y = b[k];
      if (!y || x.unread !== y.unread || x.mentionCount !== y.mentionCount || x.muted !== y.muted) return false;
    }
    return true;
  };

  const publish = (force = false): void => {
    const next = core.display(now());
    if (!force && sameRows(rows, next)) return;
    rows = next;
    for (const fn of [...subs]) { try { fn(); } catch { /* a subscriber's problem */ } }
  };

  const setStatus = (s: UnreadStatus): void => {
    if (status === s) return;
    status = s;
    publish(true);
  };

  // ── truth ────────────────────────────────────────────────────────────────
  const readTruthNow = async (): Promise<void> => {
    if (!userId) return;
    const mine = run;
    truthInFlight = true;
    truthLastAt = now();
    const token = core.beginTruth();
    try {
      const data = await deps.fetchTruth();
      if (mine !== run) return;
      core.applyTruth(data || [], token);
      const flagged = (data || []).find((r) => typeof r?.hints_enabled === 'boolean');
      if (flagged) hints = !!flagged.hints_enabled;
      schemaMissing = false;
      status = 'ready';
      publish(true);
    } catch (e) {
      if (mine !== run) return;
      // Background read: never surfaced. Missing migration, or a failure before
      // we ever had the truth → today's path takes over. A failure AFTER we had
      // it keeps the last good rows (better than blank badges).
      const missing = deps.isMissingSchema(e);
      if (missing) schemaMissing = true;
      if (missing || status !== 'ready') setStatus('unavailable');
    } finally {
      if (mine === run) {
        truthInFlight = false;
        if (truthQueued) { truthQueued = false; scheduleTruth(); }
      }
    }
  };

  const scheduleTruth = (): void => {
    if (!userId) return;
    if (truthInFlight) { truthQueued = true; return; }
    if (truthTimer) return;
    const wait = Math.max(0, UNREAD_TRUTH_MIN_GAP_MS - (now() - truthLastAt));
    if (wait === 0) { void readTruthNow(); return; }
    truthTimer = setTimeout(() => { truthTimer = null; void readTruthNow(); }, wait);
  };

  // ── the cursor write ─────────────────────────────────────────────────────
  const clearWriteTimer = (roomId: string): void => {
    const t = writeTimers.get(roomId);
    if (t) { clearTimeout(t); writeTimers.delete(roomId); }
  };

  const fireWrite = async (roomId: string, attempt: number): Promise<void> => {
    writeTimers.delete(roomId);
    if (!userId) return;
    const mine = run;
    const p = core.getPending(roomId);
    if (!p || p.origin !== 'local' || p.settled) return;
    if (canWrite.get(roomId) === false) return; // released by the next report
    const target = p.cursorMs;
    try {
      await deps.markRead(roomId, target);
      if (mine !== run) return;
      core.settlePending(roomId, target);
      // The hint normally lands first and has already dropped the pending mark.
      // If it did not (no hints on this database, a lost one), ask.
      setTimeout(() => {
        if (mine !== run) return;
        const still = core.getPending(roomId);
        if (still && still.settled) scheduleTruth();
      }, UNREAD_SETTLE_RECHECK_MS);
      // The person kept reading while the write was in flight.
      const after = core.getPending(roomId);
      if (after && !after.settled && after.origin === 'local' && !writeTimers.has(roomId)) {
        writeTimers.set(roomId, setTimeout(() => { void fireWrite(roomId, 0); }, UNREAD_MARK_READ_DEBOUNCE_MS));
      }
    } catch (e) {
      if (mine !== run) return;
      if (isTransient(e) && !deps.isMissingSchema(e) && attempt + 1 < UNREAD_WRITE_RETRIES) {
        const backoff = Math.min(30000, 2000 * 2 ** attempt);
        if (!writeTimers.has(roomId)) {
          writeTimers.set(roomId, setTimeout(() => { void fireWrite(roomId, attempt + 1); }, backoff));
        }
        return;
      }
      // A real refusal (or retries exhausted): stop pretending, ask the server.
      core.dropPending(roomId, target);
      try { deps.crossTab?.post({ type: 'read_failed', userId, roomId, cursorMs: target }); } catch { /* best effort */ }
      publish();
      scheduleTruth();
    }
  };

  const scheduleWrite = (roomId: string): void => {
    // Debounced: a burst of messages into the open room is ONE write, ~1 s
    // after the last of them.
    clearWriteTimer(roomId);
    writeTimers.set(roomId, setTimeout(() => { void fireWrite(roomId, 0); }, UNREAD_MARK_READ_DEBOUNCE_MS));
  };

  const clearRemoteTimer = (roomId: string): void => {
    const t = remoteTimers.get(roomId);
    if (t) { clearTimeout(t); remoteTimers.delete(roomId); }
  };

  const onCrossTab = (msg: CrossTabMessage): void => {
    if (!msg || !userId || msg.userId !== userId) return;
    if (msg.type === 'read') {
      if (!core.optimisticRead(msg.roomId, msg.cursorMs, 'remote')) return;
      clearRemoteTimer(msg.roomId);
      const mine = run;
      remoteTimers.set(msg.roomId, setTimeout(() => {
        remoteTimers.delete(msg.roomId);
        if (mine !== run) return;
        const p = core.getPending(msg.roomId);
        if (p && p.origin === 'remote') {
          core.dropPending(msg.roomId);
          publish();
          scheduleTruth();
        }
      }, UNREAD_REMOTE_PENDING_MS));
      publish();
    } else if (msg.type === 'read_failed') {
      const p = core.getPending(msg.roomId);
      if (p && p.origin === 'remote' && core.dropPending(msg.roomId, msg.cursorMs)) {
        clearRemoteTimer(msg.roomId);
        publish();
        scheduleTruth();
      }
    }
  };

  const clearAllTimers = (): void => {
    if (truthTimer) { clearTimeout(truthTimer); truthTimer = null; }
    if (safetyTimer) { clearInterval(safetyTimer); safetyTimer = null; }
    for (const t of writeTimers.values()) clearTimeout(t);
    for (const t of remoteTimers.values()) clearTimeout(t);
    writeTimers.clear();
    remoteTimers.clear();
    canWrite.clear();
  };

  const store: UnreadStore = {
    start(id: string) {
      if (userId === id) return;
      store.stop();
      run += 1;
      userId = id;
      core = new UnreadCore();
      hints = null;
      schemaMissing = false;
      status = 'loading';
      truthLastAt = -Infinity;
      safetyTicks = 0;
      offCrossTab = deps.crossTab ? deps.crossTab.listen(onCrossTab) : null;
      safetyTimer = setInterval(() => {
        safetyTicks += 1;
        if (!isVisible()) return;
        if (status === 'unavailable' && safetyTicks % UNREAD_UNAVAILABLE_PROBE_TICKS !== 0) return;
        scheduleTruth();
      }, UNREAD_SAFETY_MS);
      publish(true);
      void readTruthNow();
    },
    stop() {
      if (!userId && status === 'idle') return;
      run += 1;
      userId = null;
      clearAllTimers();
      if (offCrossTab) { try { offCrossTab(); } catch { /* ignore */ } offCrossTab = null; }
      truthInFlight = false;
      truthQueued = false;
      core = new UnreadCore();
      hints = null;
      status = 'idle';
      publish(true);
    },
    status: () => status,
    hintsEnabled: () => hints,
    subscribe(fn) {
      subs.add(fn);
      return () => { subs.delete(fn); };
    },
    getRows: () => rows,
    refresh: () => scheduleTruth(),
    noteRoomsChanged() {
      // With hints flowing, a rooms-list change tells us nothing the hint did
      // not. Without them (channel down / refused / no realtime.send) this is
      // the same trigger today's summary RPC runs on, so badges stay as fresh
      // as they are today — on a cheaper read.
      if (status === 'unavailable' && schemaMissing) return;
      if (channelLive() && hints === true) return;
      scheduleTruth();
    },
    onHint(payload) {
      if (!userId) return;
      if (core.applyHint(payload as UnreadHint)) {
        if (hints === null) hints = true;
        publish();
      }
    },
    onRoomRemoved(payload) {
      const roomId = (payload as any)?.room_id;
      if (!userId || !roomId) return;
      clearWriteTimer(String(roomId));
      clearRemoteTimer(String(roomId));
      if (core.removeRoom(String(roomId))) publish();
    },
    reportActive(report) {
      if (!userId || !report?.roomId) return;
      const { roomId, visible, atBottom, newestMs } = report;
      core.setActive({ roomId, visible, atBottom });
      const mayWrite = report.canWrite !== false;
      const wasHeld = canWrite.get(roomId) === false;
      canWrite.set(roomId, mayWrite);
      if (visible && atBottom && newestMs > 0) {
        // §1.4-1: 0 in THIS tick, before any network.
        if (core.optimisticRead(roomId, newestMs, 'local')) {
          try { deps.crossTab?.post({ type: 'read', userId, roomId, cursorMs: newestMs }); } catch { /* best effort */ }
          clearRemoteTimer(roomId);
          if (mayWrite) scheduleWrite(roomId);
        } else if (mayWrite && wasHeld && core.getPending(roomId)?.origin === 'local' && !writeTimers.has(roomId)) {
          scheduleWrite(roomId); // the hold was just released
        }
      }
      publish();
    },
    clearActive(roomId) {
      if (core.getActive()?.roomId !== roomId) return;
      core.setActive(null);
      // A pending write still lands: the person DID see those messages.
      publish();
    },
    setMuted(roomId, untilMs) {
      if (!userId) return;
      if (core.setMuted(roomId, untilMs)) publish();
    },
    wake() {
      if (!userId) return;
      // Mute expiry and "watching" both depend on things that may have moved.
      publish();
      // No migration → only the slow probe asks again; do not knock on every focus.
      if (status === 'unavailable' && schemaMissing) return;
      scheduleTruth();
    },
  };
  return store;
}

/** BroadcastChannel transport, feature-detected. Null where the API is missing. */
export function createBrowserCrossTab(name = 'wolfdrive-unread-v2'): CrossTab | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  let bc: BroadcastChannel;
  try { bc = new BroadcastChannel(name); } catch { return null; }
  return {
    post: (msg) => { try { bc.postMessage(msg); } catch { /* closed */ } },
    listen: (cb) => {
      const h = (ev: MessageEvent) => cb(ev.data as CrossTabMessage);
      bc.addEventListener('message', h);
      return () => bc.removeEventListener('message', h);
    },
    close: () => { try { bc.close(); } catch { /* ignore */ } },
  };
}
